import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ErrorBoundary from './ErrorBoundary';
import SampleReportCTA from './SampleReportCTA';
import logger from '../utils/logger';
import HeroSection from './LandingPage/HeroSection';
import RealResultsSection from './LandingPage/RealResultsSection';
import HowAssessmentWorksSection from './LandingPage/HowAssessmentWorksSection';
import WhatYoullDiscoverSection from './LandingPage/WhatYoullDiscoverSection';
import ChooseYourPathSection from './LandingPage/ChooseYourPathSection';
import StartYourJourneySection from './LandingPage/StartYourJourneySection';
import IndustrySectorsSection from './LandingPage/IndustrySectorsSection';
import FinalCTASection from './LandingPage/FinalCTASection';

// Main Landing Page Component
const LandingPage = () => {
  const [assessmentCounts, setAssessmentCounts] = useState({
    'Food & Beverage': 1539,
    'Plastics & Packaging': 1058, 
    'Metal Products': 1141,
    'Chemicals & Pharmaceuticals': 748,
    'Mining & Primary Metals': 313,
    'Facilities Management': 27
  });
  const [loading, setLoading] = useState(true);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  // Fetch dynamic assessment counts on component mount
  useEffect(() => {
    const fetchAssessmentCounts = async () => {
      try {
        setLoading(true);
        
        // Define historical baselines
        const historicalBaselines = {
          'Food & Beverage': 1539,
          'Plastics & Packaging': 1058,
          'Metal Products': 1141, 
          'Chemicals & Pharmaceuticals': 748,
          'Mining & Primary Metals': 313,
          'Facilities Management': 27
        };

        // Fetch dynamic counts for each industry
        const industries = Object.keys(historicalBaselines);
        const countPromises = industries.map(async (industry) => {
          try {
            const response = await fetch(`${backendUrl}/api/assessment-counts/${encodeURIComponent(industry)}`);
            if (response.ok) {
              const data = await response.json();
              return { industry, count: data.count || 0 };
            }
            return { industry, count: 0 };
          } catch (error) {
            logger.warn(`Failed to fetch count for ${industry}:`, error);
            return { industry, count: 0 };
          }
        });

        const dynamicCounts = await Promise.all(countPromises);
        
        // Calculate total counts (historical + dynamic)
        const updatedCounts = {};
        dynamicCounts.forEach(({ industry, count }) => {
          updatedCounts[industry] = historicalBaselines[industry] + count;
        });

        setAssessmentCounts(updatedCounts);
        
      } catch (error) {
        logger.error('Error fetching assessment counts:', error);
        // Keep using historical data as fallback
      } finally {
        setLoading(false);
      }
    };

    fetchAssessmentCounts();
  }, [backendUrl]);

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <HeroSection />
        <RealResultsSection />
        <HowAssessmentWorksSection />
        <WhatYoullDiscoverSection />


        <div className="py-12 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SampleReportCTA />
          </div>
        </div>
        <ChooseYourPathSection />
        <StartYourJourneySection />
        <IndustrySectorsSection assessmentCounts={assessmentCounts} loading={loading} />
        <FinalCTASection />
        
        {/* Consultant Portal Footer Access */}
        <div className="bg-gray-800 dark:bg-black py-8 border-t border-gray-700 dark:border-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <div className="w-8 h-8 bg-emerald-600 dark:bg-emerald-700 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">RA</span>
                </div>
                <div>
                  <div className="text-white font-medium">Rely Ability</div>
                  <div className="text-gray-400 dark:text-gray-500 text-sm">Energy Productivity Solutions</div>
                </div>
              </div>
              <Link
                to="/login"
                className="text-emerald-400 dark:text-emerald-300 hover:text-emerald-300 dark:hover:text-emerald-200 transition-colors font-medium"
              >
                Sign In →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default LandingPage;
